import React, { useState } from "react";
import PropTypes from "prop-types";
import client from "../../../shared/api/client";

const FIELDS = [
  { key: "name", label: "Full name", type: "text", required: true },
  { key: "email", label: "Email", type: "email", required: true },
  { key: "phone", label: "Phone", type: "tel", required: false },
  { key: "location", label: "Location", type: "text", required: false },
];

const AgentDetails = ({ agent: initialAgent, updateUrl, lastOutreach, canEdit }) => {
  const [agent, setAgent] = useState(initialAgent);
  const [form, setForm] = useState(initialAgent);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const canSave = (form.name || "").trim() && (form.email || "").trim();

  const startEditing = () => {
    setForm(agent);
    setErrors([]);
    setEditing(true);
  };

  const cancel = () => {
    setForm(agent);
    setErrors([]);
    setEditing(false);
  };

  const save = () => {
    if (!canSave || saving) return;
    setSaving(true);
    setErrors([]);

    client.patch(updateUrl, {
      agent: {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone ? form.phone.trim() : "",
        location: form.location ? form.location.trim() : "",
      },
    })
      .then((res) => {
        setAgent(res.data);
        setForm(res.data);
        setEditing(false);
        setSaving(false);
      })
      .catch((e) => {
        setSaving(false);
        const data = e && e.response && e.response.data;
        setErrors(Array.isArray(data) && data.length ? data : ["Could not save these changes."]);
      });
  };

  if (editing) {
    return (
      <div data-testid="agent-details-form">
        {FIELDS.map((field) => (
          <div className="setlist-field" key={field.key}>
            <label className="setlist-label" htmlFor={`agent-${field.key}`}>
              {field.label}
              {!field.required && <span className="setlist-optional"> (optional)</span>}
            </label>
            <input
              id={`agent-${field.key}`}
              type={field.type}
              className="setlist-input"
              value={form[field.key] || ""}
              autoFocus={field.key === "name"}
              onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
              data-testid={`agent-${field.key}`}
            />
          </div>
        ))}

        {errors.map((message) => (
          <div className="setlist-error" key={message} data-testid="agent-details-error">{message}</div>
        ))}

        <div className="d-flex justify-content-end mt-3">
          <button type="button" className="btn btn-link btn-sm mr-2" onClick={cancel}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!canSave || saving}
            onClick={save}
            data-testid="agent-details-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      {canEdit && (
        <button
          type="button"
          className="dash-card-action agent-details-edit"
          onClick={startEditing}
          title="Edit agent details"
          aria-label="Edit agent details"
          data-testid="agent-details-edit"
        >
          <i className="far fa-pen" />
        </button>
      )}

      <div className="d-flex align-items-center">
        <span className="roster-avatar roster-avatar-lg" aria-hidden="true">
          {(agent.name || "").trim().split(/\s+/).slice(0, 2).map((word) => word[0]).join("").toUpperCase()}
        </span>
        <div className="ml-3 min-width-0">
          <h4 className="mb-0 font-weight-bold" data-testid="agent-name">{agent.name}</h4>
          <span className="roster-subtitle">{["Agent", agent.location].filter(Boolean).join(" · ")}</span>
        </div>
      </div>

      <ul className="no-bullets pl-0 mt-4 mb-0 agent-detail-list">
        {agent.email && (
          <li>
            <i className="far fa-envelope" />
            <a href={`mailto:${agent.email}`}>{agent.email}</a>
          </li>
        )}
        {agent.phone && (
          <li><i className="fa fa-phone" />{agent.phone}</li>
        )}
        {agent.location && (
          <li><i className="fa fa-map-marker-alt" />{agent.location}</li>
        )}
        <li>
          <i className="far fa-clock" />
          {lastOutreach ? `Last outreach ${lastOutreach}` : "No outreach yet"}
        </li>
      </ul>
    </React.Fragment>
  );
};

AgentDetails.propTypes = {
  agent: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    location: PropTypes.string,
  }).isRequired,
  updateUrl: PropTypes.string.isRequired,
  lastOutreach: PropTypes.string,
  canEdit: PropTypes.bool,
};

AgentDetails.defaultProps = { lastOutreach: null, canEdit: false };

export default AgentDetails;
